import React from 'react'
import { Box, ButtonBase, Link, Tooltip } from '@material-ui/core'
import { alpha, useTheme } from '@material-ui/core/styles'
import BiotechIcon from '@material-ui/icons/Biotech'
import { Link as RouterLink } from 'react-router-dom'
import { PATH_DASHBOARD } from 'src/routes/paths'
import DnaIcon from '../AnalysisView/sampleTools/DnaIcon'
import { countsText } from './useBatches'

// Small pieces shared by the Home batch cards, the batch page and All batches.

// Germline reads in the primary accent with the DNA icon, somatic in the somatic
// accent with the microscope.
export const KindIcon = ({ kind, size = 18, sx }) => {
  const theme = useTheme()
  const somatic = kind === 'somatic'
  const Icon = somatic ? BiotechIcon : DnaIcon
  return (
    <Icon
      sx={{
        fontSize: size,
        flexShrink: 0,
        color: somatic ? theme.palette.somatic.main : theme.palette.primary.main,
        ...sx,
      }}
    />
  )
}

export const trackColor = (theme) => alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.08 : 0.1)

// Thin segmented progress: done (success), then running (primary), on a neutral
// track. Text always says the same thing, so colour is never the only signal.
export const SegmentedBar = ({ counts, height = 6, sx }) => {
  const theme = useTheme()
  const segments = [
    ['done', theme.palette.success.main],
    ['running', theme.palette.primary.main],
  ].filter(([key]) => counts?.[key] > 0)

  return (
    <Box
      role="img"
      aria-label={countsText(counts)}
      sx={{
        display: 'flex',
        gap: '2px',
        height,
        borderRadius: `${Math.min(height / 2, 4)}px`,
        overflow: 'hidden',
        bgcolor: trackColor(theme),
        ...sx,
      }}
    >
      {segments.map(([key, color]) => (
        <Box key={key} sx={{ flex: `${counts[key]} 1 0`, minWidth: 3, height: '100%', bgcolor: color }} />
      ))}
    </Box>
  )
}

// A count chip that filters a table: "Running 17". Pressed reads in the primary
// accent with no border.
export const FilterChip = ({ label, count, pressed, onClick }) => {
  const theme = useTheme()
  return (
    <ButtonBase
      onClick={onClick}
      aria-pressed={pressed}
      sx={{
        height: 30,
        px: 1.375,
        gap: 0.75,
        borderRadius: `${theme.shape.borderRadius}px`,
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.pxToRem(13.5),
        fontWeight: 500,
        whiteSpace: 'nowrap',
        color: pressed ? theme.palette.primary.main : theme.palette.text.secondary,
        bgcolor: pressed
          ? alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.12 : 0.16)
          : 'transparent',
        boxShadow: pressed ? 'none' : `inset 0 0 0 1px ${theme.palette.divider}`,
        '&:hover': { bgcolor: pressed ? undefined : theme.palette.action.hover },
        '&:focus-visible': { outline: `2px solid ${theme.palette.text.primary}`, outlineOffset: 2 },
      }}
    >
      {label}
      <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', opacity: 0.8 }}>
        {count}
      </Box>
    </ButtonBase>
  )
}

// Per-step lanes from the analysis list row (`row.steps`, built by the backend).
// A skipped phase counts as done, as in AnalysisStatus.
export const lanesOf = (row) =>
  (Array.isArray(row?.steps) ? row.steps : []).map((lane) => ({
    title: lane?.title ?? '',
    state: lane?.state === 'skipped' ? 'done' : lane?.state,
  }))

const LANE_GLYPH = { done: '✓', running: '●', pending: '○' }

const LaneList = ({ lanes }) => {
  const theme = useTheme()
  return (
    <Box sx={{ py: 0.25 }}>
      {lanes.map((lane, index) => (
        <Box
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          sx={{
            display: 'flex',
            gap: '6px',
            fontSize: '0.8125rem',
            fontWeight: 600,
            lineHeight: 1.5,
            color:
              lane.state === 'done'
                ? theme.palette.success.light
                : lane.state === 'running'
                ? theme.palette.info.light
                : 'inherit',
          }}
        >
          <span style={{ width: 12, textAlign: 'center', flexShrink: 0 }}>{LANE_GLYPH[lane.state] || '○'}</span>
          <span>{lane.title}</span>
        </Box>
      ))}
    </Box>
  )
}

// One 10 x 4 px mark per pipeline phase, with the phase list on hover.
export const StepLanes = ({ row, done }) => {
  const theme = useTheme()
  const lanes = lanesOf(row).map((lane) => (done ? { ...lane, state: 'done' } : lane))
  if (!lanes.length) return null
  const doneCount = lanes.filter((lane) => lane.state === 'done').length

  return (
    <Tooltip title={<LaneList lanes={lanes} />} arrow>
      <Box
        component="span"
        aria-label={`${doneCount} of ${lanes.length} steps done`}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: '3px', py: 0.75, flexWrap: 'wrap', maxWidth: 140 }}
      >
        {lanes.map((lane, index) => (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            component="i"
            sx={{
              display: 'block',
              width: 10,
              height: 4,
              borderRadius: '2px',
              bgcolor:
                lane.state === 'done'
                  ? theme.palette.success.main
                  : lane.state === 'running'
                  ? theme.palette.primary.main
                  : trackColor(theme),
            }}
          />
        ))}
      </Box>
    </Tooltip>
  )
}

export const Crumbs = ({ showBatches = true }) => {
  const theme = useTheme()
  const linkSx = { fontSize: theme.typography.pxToRem(13), color: 'text.secondary', fontWeight: 500 }
  return (
    <Box component="nav" aria-label="Breadcrumb" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
      <Link component={RouterLink} to={PATH_DASHBOARD.general.home} underline="hover" sx={linkSx}>
        Analyses
      </Link>
      {showBatches && (
        <>
          <Box component="span" aria-hidden sx={{ color: 'text.disabled', fontSize: theme.typography.pxToRem(13) }}>
            /
          </Box>
          <Link component={RouterLink} to={PATH_DASHBOARD.general.batches} underline="hover" sx={linkSx}>
            Batches
          </Link>
        </>
      )}
    </Box>
  )
}

export const tableCardSx = (theme) => ({
  bgcolor: 'background.paper',
  borderRadius: `${theme.shape.borderRadius}px`,
  boxShadow: theme.shadows[1],
  overflow: 'hidden',
})

export const headCellSx = (theme) => ({
  ...theme.typography.tableHead,
  color: 'text.secondary',
  whiteSpace: 'nowrap',
  py: 1.25,
})
